import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { Connect } from '../classes/connect';
import { ApiResponse } from '../interfaces/api-response';
import {
  MentionUser,
  NotificationWalleyItem,
  NotificationWalleyList,
  NotificationWalleyPreferences,
} from '../interfaces/notification-walley';
import { ConnectServerService } from './connect-server.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationWalleyService {
  private mentionUsersByArea = new Map<string, Observable<MentionUser[]>>();

  constructor(private connectServerService: ConnectServerService) {}

  getList(limit = 10, beforeId?: number): Observable<NotificationWalleyList> {
    const params: { limit: number; before_id?: number } = { limit };
    if (beforeId != null && beforeId > 0) {
      params.before_id = beforeId;
    }

    return this.connectServerService
      .getRequestLara<ApiResponse<NotificationWalleyList>>(
        Connect.urlServerLaraApi,
        'notification/list',
        params
      )
      .pipe(
        map((res) => ({
          unread_count: res?.data?.unread_count ?? 0,
          has_more: !!res?.data?.has_more,
          notifications: res?.data?.notifications ?? [],
        }))
      );
  }

  markRead(id: number): Observable<number> {
    return this.connectServerService
      .postRequest<ApiResponse<{ unread_count: number }>>(
        Connect.urlServerLaraApi,
        'notification/markRead',
        { id }
      )
      .pipe(map((res) => res?.data?.unread_count ?? 0));
  }

  markAllRead(): Observable<number> {
    return this.connectServerService
      .postRequest<ApiResponse<{ unread_count: number }>>(
        Connect.urlServerLaraApi,
        'notification/markAllRead',
        {}
      )
      .pipe(map((res) => res?.data?.unread_count ?? 0));
  }

  getPreferences(): Observable<NotificationWalleyPreferences> {
    return this.connectServerService
      .getRequestLara<ApiResponse<NotificationWalleyPreferences>>(
        Connect.urlServerLaraApi,
        'notification/preferences',
        {}
      )
      .pipe(
        map((res) => ({
          company_enabled: res?.data?.company_enabled ?? 0,
          enabled: res?.data?.enabled ?? 0,
          areas: res?.data?.areas ?? [],
        }))
      );
  }

  savePreferences(
    enabled: number,
    areas: { id: number; enabled: number }[]
  ): Observable<NotificationWalleyPreferences> {
    return this.connectServerService
      .postRequest<ApiResponse<NotificationWalleyPreferences>>(
        Connect.urlServerLaraApi,
        'notification/savePreferences',
        { enabled, areas }
      )
      .pipe(
        map((res) => ({
          company_enabled: res?.data?.company_enabled ?? 0,
          enabled: res?.data?.enabled ?? 0,
          areas: res?.data?.areas ?? [],
        }))
      );
  }

  getMentionUsers(area: string): Observable<MentionUser[]> {
    const key = area || '_all';
    let cached = this.mentionUsersByArea.get(key);
    if (!cached) {
      cached = this.connectServerService
        .getRequestLara<ApiResponse<{ users: MentionUser[] }>>(
          Connect.urlServerLaraApi,
          'notification/mentionUsers',
          { area }
        )
        .pipe(
          map((res) => res?.data?.users ?? []),
          shareReplay(1)
        );
      this.mentionUsersByArea.set(key, cached);
    }
    return cached;
  }

  applyIncoming(
    current: NotificationWalleyItem[],
    incoming: NotificationWalleyItem
  ): NotificationWalleyItem[] {
    if (current.some((item) => item.id === incoming.id)) {
      return current;
    }
    return [incoming, ...current];
  }
}
